import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PVData } from '../cl-image-upload-table-data.component';
import { ClImageUploadTableDataService } from '../../cl-image-upload-table-data.service';
import { utils } from 'src/app/modules/libs/utils';
import { PVInfoService } from 'src/app/services/pv-info/pv-info.service';
import { debounceTime, distinctUntilChanged, Observable, Subscription } from 'rxjs';
import { PV } from 'src/app/services/pv-info/pv-info';
import { Store } from '@ngxs/store';
import { PVRefresh } from 'src/app/services/pv-info/store/pv-info.action';
import { environment } from 'src/environments/environment';
import { Servers } from 'src/app/common/enumerations/servers-enum';
import { AlertService } from 'src/app/services/alert/alert.service';

@Component({
  selector: 'app-cl-editor-table-channels-add',
  templateUrl: './cl-editor-table-channels-add.component.html',
  styleUrls: ['./cl-editor-table-channels-add.component.scss']
})
export class ClEditorTableChannelsAddComponent implements OnInit {

  public formGroup!: FormGroup;
  public get nameCtrl(): AbstractControl | null { return this.formGroup.get('nameCtrl') ?? null; }
  public get labelCtrl(): AbstractControl | null { return this.formGroup.get('labelCtrl') ?? null; }

  pVs?: PV[];
  pVsFiltered?: PV[];
  pVs$!: Observable<PV[]>;
  pVsSubs!: Subscription;

  value?: string | null;
  title?: string;
  valid: boolean = false;
  validPV: boolean = false;
  loading: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<ClEditorTableChannelsAddComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { channel: PVData, channels: PVData[]; },
    private formBuilder: FormBuilder,
    private service: ClImageUploadTableDataService,
    private pvService: PVInfoService,
    private store: Store,
    private alert: AlertService
  ) { }

  ngOnInit(): void {
    this.title = this.data?.channel ? 'Edit' : 'Add';
    this.initializeForm();
    this.loadPVs();

    // Retrieve initial value if editing
    const trimmed = utils.stripSpaces(this.data.channel.name || '');
    if (trimmed && !this.value) {
      setTimeout(() => this.getData(trimmed), 0); // ensure it's deferred
    }

    // Debounced listener for name changes
    this.nameCtrl?.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(name => {
      const trimmedName = utils.stripSpaces(name || '');
      if (name !== trimmedName) {
        this.nameCtrl?.setValue(trimmedName);  // emits
      } else if (trimmedName) {
        this.getData(trimmedName);
      }
    });
  }

  initializeForm() {
    this.formGroup = this.formBuilder.group({
      nameCtrl: [{ value: '' }, [Validators.required, utils.IsWhiteSpaceReactiveForm]],
      labelCtrl: [{ value: '' }, [Validators.required]]
    });
    if (!this.data?.channel) {
      this.data.channel = { pos: 99, name: '', label: '' };
    }
    this.nameCtrl?.setValue(this.data.channel.name);
    this.labelCtrl?.setValue(this.data.channel.label);
  }

  loadPVs() {
    this.store.dispatch(new PVRefresh());
    this.pVs$ = this.store.select(state => state.PV.data);
    this.pVsSubs = this.pVs$.subscribe(data => {
      if (data?.length) {
        this.pVs = data;
        if (this.data?.channel?.name) {
          const pv = this.pVs.find(p => p.pvName == this.data.channel.name);
          if (!pv || pv.value == null) {
            this.validPV = false;
            this.value = 'Invalid';
          } else {
            const name = pv.pvName;
            if (pv.type === 'RSS' || name.includes(':RSSOnline')) {
              this.value = pv.value == '1' ? 'Online' : 'Offline';
            } else if (pv.type === 'PSS' || name.includes(':IsOpen')) {
              this.value = pv.value == '1' ? 'Open' : 'Closed';
            } else {
              this.value = this.service.formatValue(+pv.value);
            }
            this.validPV = true;
          }
          this.evaluateValidity();
        }
      }
    });
  }

  getData(name: string): void {
    this.loading = true;
    this.value = '';
    this.nameCtrl?.setErrors(null);
    // const pv = this.pVs?.find(p => p.pvName === name);

    this.pvService.subscribe(name).subscribe({
      next: data => {
        if (data && !data.value.includes('ERROR')) {
          this.validPV = true;
          if (data.type == 'RSS' || data.pvName.includes(':RSSOnline')) { this.value = data.value == '1' ? 'Online' : 'Offline'; }
          else if (data.type == 'PSS' || data.pvName.includes(':IsOpen')) { this.value = data.value == '1' ? 'Open' : 'Closed'; }
          else { this.value = this.service.formatValue(+data.value); }
          this.nameCtrl?.setErrors(null);
          this.evaluateValidity();
          if (!environment.isProduction)
            this.alert.info("Fake PV Value for Testing purposes in Non Production Environment");
        } else {
          this.validPV = false;
          this.value = '';
          this.nameCtrl?.setErrors({ invalid: true });
        }
        this.loading = false;
        this.evaluateValidity();
      },
      error: err => {

        // For local testing:
        // console.log('PV fetch error (bypassed):', err);
        // this.validPV = true;
        // this.nameCtrl?.setErrors(null);
        // this.evaluateValidity();

        this.nameCtrl?.setErrors({ invalid: true });
        console.log('PV fetch error:', err);
        this.loading = false;
      }
    });
  }

  evaluateValidity(): boolean {
    const name = utils.stripSpaces(this.nameCtrl?.value || '');
    const label = this.labelCtrl?.value || '';
    const nameExists = this.data.channels.some(
      x => x.name === name && name !== this.data.channel.name // exclude original
    );
    if (nameExists) { this.nameCtrl?.setErrors({ exists: true }); }
    else { this.nameCtrl?.setErrors(null); }

    this.valid = !!name && !!label && this.validPV && !nameExists;

    const disableSave = !this.formGroup.valid || !this.valid || !this.formGroup.dirty || this.loading;
    return disableSave;
  }

  save() {
    this.data.channel.name = utils.stripSpaces(this.nameCtrl?.value) ?? '';
    this.data.channel.label = this.labelCtrl?.value;
    this.data.channel.value = null;
    setTimeout(() => {
      console.log('About to close dialog with channel:', this.data.channel);
      this.dialogRef.close(this.data.channel);
    }, 500);
  }

  getErrorMsg(control?: AbstractControl | null): string | null {
    if (control?.hasError('required')) { return 'You must enter a value'; }
    if (control?.hasError('hasWhiteSpace')) { return 'Enter a valid value'; }
    if (control?.hasError('invalid')) { return 'Invalid Channel name'; }
    if (control?.hasError('exists')) { return 'This PV is already in use'; }
    return null;
  }

}
